export class OperatorAuthApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /**
     * Authenticate an operator (credential + MFA). A wrong credential/MFA is a uniform 401. Carries a device-key proof
     * when the client has a proof provider, binding the session's refresh to that key (ADR-0039).
     */
    login(input) {
        return this.client.request({ method: 'POST', path: '/v1/operator/login', body: input, deviceProof: {} });
    }
    /** D449 (A-06): sign in with a recovery code (lost authenticator). The code is spent on success; a reuse is a uniform 401. */
    loginWithRecoveryCode(input) {
        return this.client.request({ method: 'POST', path: '/v1/operator/login', body: input, deviceProof: {} });
    }
    /**
     * Initiate operator account recovery (S111). Anti-enumeration on the server: the result is always `accepted`
     * whether or not the identifier maps to an operator, so the UI must not reveal whether the account exists.
     */
    recover(identifier, console) {
        // D449 (X-10): the console names where the reset link lands (the server ignores an unknown one → the Admin).
        return this.client.request({ method: 'POST', path: '/v1/operator/recovery', body: { identifier, ...(console !== undefined ? { console } : {}) } });
    }
    /**
     * Complete account recovery: set a new password from the single-use token delivered by the recovery email
     * (Phase 2). The token is the secret, so an invalid/expired/used token is a uniform 400 (no enumeration).
     */
    completeRecovery(input) {
        return this.client.request({ method: 'POST', path: '/v1/operator/recovery/complete', body: input });
    }
    /**
     * The operator's own scoped grants — GLOBAL / COUNTRY / a CITY|MARKET `cc:slug`, and since D355 the persona tenant
     * kinds MERCHANT (a merchant id) / FLEET (a fleet organization id) — feeding the scope selector, with the permission
     * words each grant carries and the distinct scope kinds held.
     */
    grants() {
        return this.client.request({ method: 'GET', path: '/v1/operator/grants', auth: true });
    }
    /**
     * D449 (A-05/A-06): open an invitation (or a second-factor reset) from its link. The server mints the operator's OWN
     * authenticator secret (sealed at rest) and shows it here; an invalid or expired token is a uniform 400 INVITATION_INVALID.
     */
    inspectInvitation(token) {
        return this.client.request({ method: 'POST', path: '/v1/operator/invitations/inspect', body: { token } });
    }
    /**
     * D449: accept an invitation with the first authenticator code (and the chosen password for an ENROLL invitation). The
     * single-use recovery codes come back ONCE, here.
     */
    acceptInvitation(input) {
        return this.client.request({ method: 'POST', path: '/v1/operator/invitations/accept', body: input });
    }
    /** D449 (A-06): the signed-in operator's second-factor state. */
    mfaStatus() {
        return this.client.request({ method: 'GET', path: '/v1/operator/mfa', auth: true });
    }
    /** D449 (A-06): start the operator's own authenticator (e.g. leaving the shared legacy secret); confirmed by its first code. */
    startMfaEnrollment() {
        return this.client.request({ method: 'POST', path: '/v1/operator/mfa/enrollments', body: {}, auth: true });
    }
    /**
     * D449 (A-06): confirm the enrollment with its first code; fresh recovery codes come back ONCE. D502 (Q-005): the swap also proves the CURRENT
     * factor — `currentCode` (a current code of the authenticator in use) or `recoveryCode` (an unused one), single use.
     */
    confirmMfaEnrollment(enrollmentId, input) {
        return this.client.request({ method: 'POST', path: `/v1/operator/mfa/enrollments/${encodeURIComponent(enrollmentId)}/confirm`, body: { code: input.code, ...(input.currentCode !== undefined ? { currentCode: input.currentCode } : {}), ...(input.recoveryCode !== undefined ? { recoveryCode: input.recoveryCode } : {}) }, auth: true });
    }
    /** D449 (A-06): replace the recovery codes (the current authenticator code proves presence); the old ones stop working. */
    regenerateRecoveryCodes(mfaCode) {
        return this.client.request({ method: 'POST', path: '/v1/operator/mfa/recovery-codes', body: { mfaCode }, auth: true });
    }
    /**
     * D805 (doc 35 §3.1): change the operator's own password - the current password and a current authenticator code. On success every OTHER session of the
     * account ends (this one stays). Stable refusals: `OPERATOR_PASSWORD_FIELDS_REQUIRED`, `PASSWORD_POLICY`, `OPERATOR_PASSWORD_UNCHANGED`,
     * `MFA_CODE_INVALID`, `OPERATOR_PASSWORD_CURRENT_INVALID` (400); `OPERATOR_SESSION_REQUIRED` (403); `OPERATOR_PASSWORD_UNAVAILABLE` (409 / 503);
     * `OPERATOR_PASSWORD_THROTTLED` (429, with `retryAfterSeconds`); `OPERATOR_PASSWORD_THROTTLE_UNAVAILABLE`, `AUTH_BUSY` (503).
     */
    changePassword(input) {
        return this.client.request({ method: 'POST', path: '/v1/operator/password', body: input, auth: true });
    }
    /** D805: the operator's own ACTIVE sessions of this console realm, this one first (`current: true`). */
    sessions() {
        return this.client.request({ method: 'GET', path: '/v1/operator/sessions', auth: true });
    }
    /** D805: end every OTHER session of the account ("sair de todos os outros"); this one stays. */
    revokeOtherSessions() {
        return this.client.request({ method: 'POST', path: '/v1/operator/sessions/revoke-others', body: {}, auth: true });
    }
    /**
     * D805: end one OTHER session. This one is `OPERATOR_SESSION_IS_CURRENT` (409 - sign out instead); an id that is not this account's session is
     * `OPERATOR_SESSION_NOT_FOUND` (404); an already ended one answers `revoked: false`.
     */
    revokeSession(sessionId) {
        return this.client.request({ method: 'POST', path: `/v1/operator/sessions/${encodeURIComponent(sessionId)}/revoke`, body: {}, auth: true });
    }
    /** D805 part B: the operator's own optional photo (`{ photo: null }` without one). */
    photo() {
        return this.client.request({ method: 'GET', path: '/v1/operator/photo', auth: true });
    }
    /**
     * D805 part B: set the operator's own photo - a JPEG as base64 (the server strips its metadata, scans it and keeps it encrypted; no face check, no review).
     * Stable refusals: `OPERATOR_PHOTO_BODY_INVALID`, `OPERATOR_PHOTO_TYPE_INVALID`, `OPERATOR_PHOTO_TOO_LARGE`, `OPERATOR_PHOTO_TOO_SMALL`,
     * `OPERATOR_PHOTO_MALFORMED`, `OPERATOR_PHOTO_MALWARE` (400; a body past the upload limit is `REQUEST_BODY_TOO_LARGE`); `OPERATOR_SESSION_REQUIRED` (403);
     * `OPERATOR_PHOTO_RATE_LIMITED` (429, with `retryAfterSeconds`); `OPERATOR_PHOTO_STORE_UNAVAILABLE` (503, nothing changed).
     */
    setPhoto(bytesBase64) {
        return this.client.request({ method: 'PUT', path: '/v1/operator/photo', body: { contentType: 'image/jpeg', bytesBase64 }, auth: true });
    }
    /** D805 part B: remove the operator's own photo - the console draws the initials again (idempotent). */
    removePhoto() {
        return this.client.request({ method: 'DELETE', path: '/v1/operator/photo', auth: true });
    }
}
//# sourceMappingURL=operator-auth.js.map